import { useEffect, useState } from "react"
import { useLocation } from "react-router-dom"
import { useHub } from "../../../../core/Global"
import FlexRow from "../../../../components/base/FlexRow"
import Icon from "../../../../components/base/Icon"
import { PaymentTransactionsSummary } from "../models/PaymentTransaction"

const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" })

/**
 * The totals above the list.
 *
 * It re-asks whenever the query string changes, because that is where the filters live — so the
 * totals always describe the same set of transactions the table is showing, rather than the page
 * of them that happens to be on screen.
 */
export default function TransactionSummary() {

    const hub = useHub()
    const location = useLocation()

    const [summary, summarySet] = useState<PaymentTransactionsSummary>()

    useEffect(() => {
        hub.Get("/reports/PaymentTransactions/summary" + (location.search ?? ""), res => {
            if (res?.success) summarySet(res.data)
        })
    }, [location.search])

    const Card = (props: { title: string, value: string, sub?: string, icon: any, color: string }) => (
        <div className="col">
            <div className="card h-100">
                <div className="card-body p-4">
                    <FlexRow className="mb-1">
                        <Icon icon={props.icon} className={`text-${props.color} me-2`} size={13} />
                        <span className="text-gray-600 fs-8 text-uppercase fw-bold">{props.title}</span>
                    </FlexRow>
                    <div className={`fw-bolder fs-3 text-${props.color}`}>{props.value}</div>
                    {props.sub && <div className="text-gray-500 fs-8 mt-1">{props.sub}</div>}
                </div>
            </div>
        </div>
    )

    const count = (n?: number) => `${n ?? 0} transaction${(n ?? 0) === 1 ? "" : "s"}`

    return (
        <div className="row row-cols-2 row-cols-md-5 g-3 mb-5">
            <Card title="Approved"
                icon="circle-check"
                color="success"
                value={money.format(summary?.approvedAmount ?? 0)}
                sub={count(summary?.approvedCount)} />

            {/* The approved total again, split by where the card was presented — the two add up
                to it, which is what makes the split worth reading. */}
            <Card title="Tap to Pay"
                icon="wifi"
                color="dark"
                value={money.format(summary?.tapToPayAmount ?? 0)}
                sub={count(summary?.tapToPayCount)} />

            <Card title="Online Payments"
                icon="credit-card"
                color="dark"
                value={money.format(summary?.webCardAmount ?? 0)}
                sub={count(summary?.webCardCount)} />

            <Card title="Tips"
                icon="hand-holding-dollar"
                color="primary"
                value={money.format(summary?.tipAmount ?? 0)}
                sub="Included in the approved total" />

            <Card title="Refunded"
                icon="rotate-left"
                color="warning"
                value={money.format(summary?.refundedAmount ?? 0)}
                sub={`${summary?.refundedCount ?? 0} refund${(summary?.refundedCount ?? 0) === 1 ? "" : "s"}`} />
        </div>
    )
}
